import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  X,
  GraduationCap,
  Calendar,
  BookOpen,
  CheckCircle2,
  AlertCircle,
  Building2,
  Award,
  Layers,
  User,
  Printer
} from 'lucide-react';

interface StudentHistoryRecord {
  month: string;
  subject: string;
  subject_code: string;
  type: string;
  maximum_marks: number;
  obtained_marks: number | string | null;
  percentage: number | null;
  status: 'PRESENT' | 'ABSENT' | 'NOT_APPLICABLE';
  date: string | null;
}

export const StudentAcademicHistoryModal: React.FC<{
  studentId: number | null;
  onClose: () => void;
}> = ({ studentId, onClose }) => {
  const { fetchWithAuth } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [student, setStudent] = useState<any>(null);
  const [history, setHistory] = useState<StudentHistoryRecord[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>('all');

  useEffect(() => {
    if (!studentId) return;
    const fetchHistory = async () => {
      setLoading(true);
      try {
        const res = await fetchWithAuth(`/api/students/${studentId}/academic-history`);
        const json = await res.json();
        if (json.success) {
          setStudent(json.data?.student || null);
          setHistory(json.data?.history || []);
        }
      } catch (e) {
        console.error('Error loading student academic history:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [studentId]);

  if (!studentId) return null;

  const filteredHistory = selectedMonth === 'all'
    ? history
    : history.filter(h => h.month === selectedMonth);

  const totalAssessed = history.filter(h => h.status === 'PRESENT' && h.percentage !== null);
  const avgOverallScore = totalAssessed.length > 0
    ? Number((totalAssessed.reduce((sum, h) => sum + (h.percentage || 0), 0) / totalAssessed.length).toFixed(1))
    : 0;

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-in fade-in">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-slate-200">
        {/* Modal Header */}
        <div className="bg-[#0F2C59] text-white p-4 flex justify-between items-start">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-amber-400 text-[#0F2C59] flex items-center justify-center font-black text-lg">
              <GraduationCap className="w-6 h-6" />
            </div>
            <div>
              <div className="text-[11px] font-bold text-amber-400 uppercase tracking-wider">
                Student Academic Performance Dossier
              </div>
              <h2 className="text-lg font-bold">
                {student?.student_name || 'Loading Student...'}
              </h2>
              <div className="text-xs text-slate-300">
                SRN: <span className="font-mono font-bold text-white">{student?.srn}</span> • {student?.class_name} {student?.stream ? `(${student?.stream})` : ''} • {student?.school_name}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => window.print()}
              className="p-1.5 bg-[#081B38] hover:bg-slate-700 text-slate-200 rounded text-xs flex items-center gap-1"
              title="Print Dossier"
            >
              <Printer className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 bg-[#081B38] hover:bg-rose-700 text-slate-200 hover:text-white rounded transition"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Student Quick Facts & Summary */}
        <div className="p-4 bg-slate-50 border-b border-slate-200 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div className="bg-white p-2.5 rounded border border-slate-200">
            <div className="text-[10px] text-slate-500 font-bold uppercase">Father's Name</div>
            <div className="font-bold text-slate-900 mt-0.5">{student?.father_name || 'N/A'}</div>
          </div>
          <div className="bg-white p-2.5 rounded border border-slate-200">
            <div className="text-[10px] text-slate-500 font-bold uppercase">Block</div>
            <div className="font-bold text-slate-900 mt-0.5">{student?.block || 'N/A'}</div>
          </div>
          <div className="bg-white p-2.5 rounded border border-slate-200">
            <div className="text-[10px] text-slate-500 font-bold uppercase">Tests Evaluated</div>
            <div className="font-bold text-blue-900 mt-0.5">{history.length} Records</div>
          </div>
          <div className="bg-white p-2.5 rounded border border-slate-200">
            <div className="text-[10px] text-slate-500 font-bold uppercase">Overall Cumulative %</div>
            <div className={`font-black text-sm mt-0.5 ${avgOverallScore >= 60 ? 'text-emerald-700' : avgOverallScore >= 40 ? 'text-blue-700' : 'text-rose-700'}`}>
              {avgOverallScore}%
            </div>
          </div>
        </div>

        {/* Filter Controls */}
        <div className="px-4 py-2 bg-white border-b border-slate-200 flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-slate-500" />
            <span className="font-semibold text-slate-700">Filter Month:</span>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="bg-slate-50 border border-slate-300 font-semibold text-slate-900 rounded px-2 py-1 focus:outline-none"
            >
              <option value="all">All Months (May, July, August, September)</option>
              <option value="May">May 2026</option>
              <option value="July">July 2026</option>
              <option value="August">August 2026</option>
              <option value="September">September 2026</option>
            </select>
          </div>
          <div className="text-slate-500 text-[11px]">
            Showing {filteredHistory.length} subject test assessments
          </div>
        </div>

        {/* History Table */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="py-12 text-center text-slate-500 text-xs">
              Loading student academic records from database...
            </div>
          ) : filteredHistory.length > 0 ? (
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-100 text-slate-700 uppercase font-bold border-b border-slate-200 text-[11px]">
                <tr>
                  <th className="py-2 px-3">Month</th>
                  <th className="py-2 px-3">Subject</th>
                  <th className="py-2 px-2 text-center">Type</th>
                  <th className="py-2 px-2 text-center">Max</th>
                  <th className="py-2 px-2 text-center text-emerald-900">Obtained</th>
                  <th className="py-2 px-2 text-center">% Score</th>
                  <th className="py-2 px-3 text-center">Attendance</th>
                  <th className="py-2 px-3 text-right">Entry Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 font-medium">
                {filteredHistory.map((h, i) => (
                  <tr key={`${h.month}-${h.subject}-${i}`} className="hover:bg-slate-50">
                    <td className="py-2.5 px-3 font-bold text-slate-900 flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-blue-600" />
                      <span>{h.month}</span>
                    </td>
                    <td className="py-2.5 px-3 font-semibold text-slate-800">
                      <div>{h.subject}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{h.subject_code}</div>
                    </td>
                    <td className="py-2.5 px-2 text-center">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${h.type === 'SAT' ? 'bg-indigo-100 text-indigo-800' : 'bg-slate-100 text-slate-700'}`}>
                        {h.type}
                      </span>
                    </td>
                    <td className="py-2.5 px-2 text-center text-slate-600 font-mono">{h.maximum_marks}</td>
                    <td className="py-2.5 px-2 text-center font-black text-slate-900">
                      {h.status === 'ABSENT' ? (
                        <span className="text-rose-600">ABSENT</span>
                      ) : h.status === 'NOT_APPLICABLE' ? (
                        <span className="text-slate-400">N/A</span>
                      ) : (
                        h.obtained_marks ?? '-'
                      )}
                    </td>
                    <td className="py-2.5 px-2 text-center font-bold">
                      {h.percentage !== null ? (
                        <span className={`px-2 py-0.5 rounded text-[11px] ${
                          h.percentage >= 75 ? 'bg-emerald-100 text-emerald-800' :
                          h.percentage >= 40 ? 'bg-blue-100 text-blue-800' :
                          'bg-rose-100 text-rose-800'
                        }`}>
                          {h.percentage}%
                        </span>
                      ) : '-'}
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      {h.status === 'PRESENT' ? (
                        <span className="bg-emerald-50 text-emerald-700 font-bold px-2 py-0.5 rounded border border-emerald-200 text-[10px]">
                          Present
                        </span>
                      ) : h.status === 'ABSENT' ? (
                        <span className="bg-rose-50 text-rose-700 font-bold px-2 py-0.5 rounded border border-rose-200 text-[10px]">
                          Absent
                        </span>
                      ) : (
                        <span className="bg-slate-100 text-slate-600 font-bold px-2 py-0.5 rounded text-[10px]">
                          N/A
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right text-slate-400 font-mono text-[10px]">
                      {h.date ? new Date(h.date).toLocaleDateString('en-IN') : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="py-12 text-center text-slate-400 text-xs">
              <AlertCircle className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              <span>No historical assessment marks found for the selected month.</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 bg-slate-50 border-t border-slate-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-800 font-bold rounded text-xs transition"
          >
            Close Dossier
          </button>
        </div>
      </div>
    </div>
  );
};
